import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useContext,
} from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import cornerstone from 'cornerstone-core';
import cornerstoneTools from 'cornerstone-tools';
import OHIF from '@ohif/core';
import {
  Spin,
  Button,
  Modal,
  Input,
  Checkbox,
  Select,
  Popconfirm,
  Alert,
  Form,
  Pagination,
  message,
} from 'antd';
import { UpOutlined, DownOutlined } from '@ant-design/icons';
import { servicesManager } from '../../App';
import useSearchParams from '../../customHooks/useSearchParams';
import {
  DrawContext,
  useIsResearch,
  useGetPropertyList,
  useGetDrawList,
  useGetImageId,
  useChangeActiveMark,
} from './hook';
import * as api from './api';
import store from '../../store';
import {
  setActiveMarkUUID,
  setPagination,
  setHideData,
  setIsHide,
  setActiveContextMenuType,
  getMarkPermissions,
  setMarkUsers,
} from '@ohif/viewer/src/store/mark/action';
import {
  setLevels,
  setActiveButtonIds,
} from '@ohif/viewer/src/store/viewer/actions';
import { useHasPermission } from '@ohif/viewer/src/store/permission/hooks';
import BackRoute from '@ohif/viewer/src/research/components/backRoute';

import './markList.less';

// 切换阅片器图层
const changeViewport = (
  activeViewportIndex,
  StudyInstanceUID,
  SeriesInstanceUID,
  SOPInstanceUID
) => {
  return new Promise((resolve, reject) => {
    if (!StudyInstanceUID) return;
    const studyMetadata = OHIF.utils.studyMetadataManager.get(StudyInstanceUID);
    let allDisplaySets = studyMetadata.getDisplaySets();
    if (!allDisplaySets.length)
      allDisplaySets = studyMetadata._data.displaySets;
    const displaySet = allDisplaySets.find(
      item => item.SeriesInstanceUID === SeriesInstanceUID
    );
    if (!displaySet) return console.warn('未找到相应的 displaySet');
    if (SOPInstanceUID) displaySet.SOPInstanceUID = SOPInstanceUID;
    const unsubscribe = store.subscribe(resolve);
    store.dispatch(
      OHIF.redux.actions.setViewportSpecificData(
        activeViewportIndex,
        displaySet
      )
    );
    unsubscribe();
  });
};

const Mark = ({ item, disabled, onChange = new Function(), ...props }) => {
  const propertiesText = useMemo(
    () =>
      item.properties.properties.map(data => ({
        name: data.name,
        text: `${data.name}: ${data.value.name}`,
      })),
    [item.properties.properties]
  );
  const handleUpdate = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();
      onChange('update', item);
    },
    [item, onChange]
  );
  const handleRemove = useCallback(() => onChange('remove', item), [
    item,
    onChange,
  ]);

  const hideDisable = useMemo(() => propertiesText.length > 4, [
    propertiesText.length,
  ]);
  const [isHide, setIsHide] = useState(hideDisable);
  const hideClass = useMemo(() => (isHide ? '' : 'show'), [isHide]);
  const changeHide = useCallback(() => setIsHide(!isHide), [isHide]);

  return (
    <div className="mark-main mark-bg" {...props}>
      <div className="viewer-mark-title">
        <span>{item.properties.lesion.name}</span>
        <span>
          标注者: {item.source.account && item.source.account.userName}
        </span>
      </div>
      <div className={`mark-content ${hideClass}`}>
        {hideDisable && (
          <div className="fold" onClick={changeHide}>
            {isHide ? <UpOutlined /> : <DownOutlined />}
          </div>
        )}
        {propertiesText.map((property, index) => (
          <span key={index} title={property.text}>
            {property.text}
          </span>
        ))}
      </div>
      <div className="action">
        {!disabled && (
          <>
            <span onClick={handleUpdate}>编辑</span>
            <Popconfirm title="删除后将无法恢复!" onConfirm={handleRemove}>
              <span>删除</span>
            </Popconfirm>
          </>
        )}
        <div>
          <span>Ser: {item.source.ser}</span>
          <span>Img: {item.source.img}</span>
        </div>
      </div>
    </div>
  );
};

Mark.propTypes = {
  item: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
};

const applyText = {
  10: ['撤回通过', '撤回成功!'],
  20: ['审核通过', '审核通过!'],
  30: ['审核驳回', '审核不通过!'],
};

const ApplyDialog = ({ type, users, onCancel, onChange, ...props }) => {
  const typeText = applyText[type][0];
  const placeholder = `请填写${typeText}原因`;
  const [form] = Form.useForm();
  const onSubmit = useCallback(form => onChange && onChange(form, type), [
    onChange,
    type,
  ]);
  return (
    <Modal
      title={typeText}
      destroyOnClose
      onCancel={onCancel}
      onOk={form.submit}
      {...props}
    >
      <Form form={form} preserve={false} onFinish={onSubmit}>
        {type === 30 && (
          <Form.Item
            name="accountIds"
            label="标注者"
            rules={[{ required: true, message: '必须选择标注者!' }]}
          >
            <Select mode="multiple" style={{ width: '200px' }}>
              {users.map(item => (
                <Select.Option key={item.accountId} value={item.accountId}>
                  {item.userName}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        )}
        <Form.Item
          name="content"
          rules={[{ required: type === 30, message: '不能为空!' }]}
        >
          <Input.TextArea placeholder={placeholder} />
        </Form.Item>
      </Form>
    </Modal>
  );
};
ApplyDialog.propTypes = {
  type: PropTypes.number,
  users: PropTypes.arrayOf(PropTypes.object),
  onCancel: PropTypes.func,
  onChange: PropTypes.func,
};

const MarkList = props => {
  const [{ aiCheckId: caseId, pro, type: menuType }] = useSearchParams();
  const {
    viewports,
    isHideList,
    isHideMark,
    hideList,
    markList,
    activeMarkUUID,
    markUsers,
  } = useSelector(
    ({
      viewports,
      mark: {
        hideData,
        isHideList,
        isHideMark,
        markList,
        activeMarkUUID,
        markUsers,
      },
    }) => ({
      viewports,
      isHideList: isHideList[caseId] || false,
      isHideMark: isHideMark[caseId] || false,
      hideList: hideData[caseId] || [],
      markList,
      activeMarkUUID,
      markUsers,
    })
  );
  const dispatch = useDispatch();

  // 隐藏列表数据管理
  const setHideList = useCallback(
    data => dispatch(setHideData({ [caseId]: data })),
    [caseId, dispatch]
  );
  const handleSetIsHide = useCallback(
    (key, data) => dispatch(setIsHide(key, { [caseId]: data })),
    [caseId, dispatch]
  );
  const getHideState = useCallback(
    hideState => ({
      indeterminate:
        hideState && !!hideList.length && hideList.length < markList.length,
      all: hideState && hideList.length === markList.length,
    }),
    [hideList.length, markList.length]
  );
  const handleHideStateChange = (checkState, key) => () => {
    const isHideState = { isHideList, isHideMark };
    const isHide = checkState.indeterminate || !isHideState[key];
    const markListIds = markList.map(item => item.source.id);

    if (
      isHide &&
      (checkState.all || checkState.indeterminate || !hideList.length)
    )
      setHideList(markListIds);
    if (!isHide) setHideList([]);

    handleSetIsHide(key, isHide);
  };
  const checkChange = value =>
    setHideList(listHide.indeterminate ? hideList.concat(value) : value);

  // 隐藏列表状态
  const listHide = getHideState(isHideList);
  // 隐藏绘图状态
  const markHide = getHideState(isHideMark);

  // 取消隐藏时无选中数据就重置隐藏状态
  useEffect(() => {
    if (isHideList && !hideList.length) handleSetIsHide('isHideList', false);
    if (isHideMark && !hideList.length) handleSetIsHide('isHideMark', false);
  }, [handleSetIsHide, hideList.length, isHideList, isHideMark]);

  // 标注成员
  // const [markUsers, setMarkUsers] = useState([]);
  const genMarkUsers = useCallback(users => dispatch(setMarkUsers(users)), [
    dispatch,
  ]);
  const [markUserOptions, setMarkUserOptions] = useState([]);
  useEffect(() => {
    caseId &&
      api
        .getMarkUsers({ imageScienceDataId: pro, caseId })
        .then(res => setMarkUserOptions(res.list || []));
  }, [caseId, pro]);

  // 过滤后的列表数据
  const excludeIds = useMemo(
    () =>
      markUsers.length
        ? markList
            .filter(
              item =>
                item.source.account &&
                !markUsers.includes(item.source.account.accountId)
            )
            .map(item => item.source.id)
        : [],
    [markList, markUsers]
  );
  const filterMarkList = useMemo(() => {
    const conditions = [];
    if (markUsers.length)
      conditions.push(
        item =>
          item.source.account &&
          markUsers.includes(item.source.account.accountId)
      );
    if (isHideList) conditions.push(item => !hideList.includes(item.source.id));
    return markList.filter(item => conditions.every(cond => cond(item)));
  }, [hideList, isHideList, markList, markUsers]);

  // 加载属性列表
  const {
    handlePropertyList,
    pagination,
    loading: propertyListLoading,
    // users: markUserOptions,
    apply,
  } = useGetPropertyList({ filterUsers: markUsers });
  const genSetPagination = useCallback(
    (current, pageSize) => dispatch(setPagination({ current, pageSize })),
    [dispatch]
  );

  useEffect(() => {
    handlePropertyList();
  }, [handlePropertyList]);
  // 初始化标注分页
  useEffect(() => () => genSetPagination(1, 100), [genSetPagination]);

  // 加载绘图数据
  const [filterIds, setFilterIds] = useState([]);
  useEffect(() => setFilterIds(excludeIds), [excludeIds]);
  useEffect(() => {
    if (isHideMark) setFilterIds(hideList.concat(excludeIds));
    if (!isHideMark && filterIds.length) setFilterIds(excludeIds);
  }, [excludeIds, filterIds.length, hideList, isHideMark, markList.length]);
  const [handleDrawList] = useGetDrawList(
    viewports,
    {
      filterIds,
      filterUsers: markUsers,
      pagination,
    },
    props
  );
  useEffect(handleDrawList, [handleDrawList]);

  const getImageId = useGetImageId();
  const changeActiveMark = useChangeActiveMark();
  const { activeViewportIndex } = viewports;
  const onMarkClick = useCallback(
    async source => {
      dispatch(setActiveMarkUUID(source.dataUuid));
      const { dataUuid, type, studyUid, serUid, sopUid } = source;
      const imageId = getImageId(studyUid, serUid, sopUid);
      await changeViewport(activeViewportIndex, studyUid, serUid, sopUid);
      await changeActiveMark(imageId, type, dataUuid);
      const element =
        cornerstoneTools.store.state.enabledElements[activeViewportIndex];
      cornerstone.updateImage(element);
    },
    [activeViewportIndex, changeActiveMark, dispatch, getImageId]
  );
  const drawContext = useContext(DrawContext);

  const onUpdate = useCallback(
    async mark => {
      if (drawContext.drawing) await drawContext.drawing(100);
      const { id, dataUuid, type } = mark.source;
      // 从选中的隐藏数据中剔除
      const hideListIndex = hideList.indexOf(id);
      if (hideListIndex >= 0) {
        hideList.splice(hideListIndex, 1);
        dispatch(setHideData({ [caseId]: [...hideList] }));
      }

      // 设置窗宽窗位
      const windowType = mark.source.windowType
        ? JSON.parse(mark.source.windowType)
        : {};
      dispatch(setLevels(windowType));

      // 设置标注工具
      cornerstoneTools.setToolActive(type, {
        mouseButtonMask: 1,
      });
      let buttonKey;
      switch (type) {
        case 'MarkFreehandRoi':
          buttonKey = 'freehand';
          break;
        case 'MarkRectangleRoi':
          buttonKey = 'rect';
          break;
      }
      if (buttonKey) dispatch(setActiveButtonIds([buttonKey]));

      // 表单数据
      const form = {
        ...mark.properties,
        lesion: (mark.properties.lesion.id || '').toString(),
        properties: mark.properties.properties.reduce(
          (acc, data) => Object.assign(acc, { [data.id]: data.value.id }),
          {}
        ),
      };

      // 需要等切换到标注的切片再进行编辑
      const { SidePanelService } = servicesManager.services;
      SidePanelService.setState({
        id,
        uuid: dataUuid,
        toolType: type,
        form,
      });
      SidePanelService.handleSidePanelChange('right', 'mark-form');
      // onMarkClick(mark);
    },
    [caseId, dispatch, drawContext, hideList]
  );
  const onRemove = useCallback(
    mark => {
      api.removeMark({ tagResultId: mark.source.id }).then(() => {
        handlePropertyList();
        handleDrawList();
      });
    },
    [handleDrawList, handlePropertyList]
  );

  const onChange = useCallback(
    async (type, mark) => {
      if (type === 'remove') {
        onRemove(mark);
      }
      if (type === 'update') {
        await onMarkClick(mark.source);
        setTimeout(() => onUpdate(mark), 300);
      }
    },
    [onMarkClick, onRemove, onUpdate]
  );

  // 右键菜单监听
  useEffect(() => {
    let prevState = null;
    const unsubscribe = store.subscribe(() => {
      const currentState = store.getState().mark.activeContextMenuType;
      if (prevState === currentState) return;
      prevState = currentState;
      dispatch(setActiveContextMenuType());
      const mark = markList.find(
        item => activeMarkUUID === item.source.dataUuid
      );
      if (!mark) return console.error('未找到标注数据');
      // 删除数据
      if (currentState === 'remove') {
        Modal.confirm({
          content: '删除后将无法恢复!',
          okType: 'danger',
          onOk: () => onRemove(mark),
        });
      }
      if (currentState === 'edit') onUpdate(mark);
    });
    return () => unsubscribe();
  }, [
    activeMarkUUID,
    caseId,
    dispatch,
    hideList,
    markList,
    onChange,
    onRemove,
    onUpdate,
  ]);

  // 选中定位
  useEffect(() => {
    const scrollElement = document.querySelector('.mark-list-main');
    const element = scrollElement.querySelector('.mark-items');
    const index = filterMarkList.findIndex(
      item => item.source.dataUuid === activeMarkUUID
    );
    if (index < 0 || index >= element.childNodes.length) return;
    const activeItem = element.childNodes[index];
    const { offsetTop, offsetHeight } = activeItem;
    const offsetDiff = offsetTop + offsetHeight - scrollElement.scrollTop;
    if (offsetDiff >= 0 && offsetDiff <= scrollElement.clientHeight) return;
    scrollElement.scrollTop = offsetTop;
  }, [activeMarkUUID, filterMarkList]);

  /* ---------------------------------- 审核流程 --------------------------------- */
  const isScience = useIsResearch();
  const [actionLoading, setActionLoading] = useState(false);
  const handleUpdateMarkEnd = useCallback(() => {
    setActionLoading(true);
    isScience &&
      api
        .updateMarkEnd({ imageDataScienceCheckId: caseId, tagStatus: '10' })
        .then(() => dispatch(getMarkPermissions(caseId, menuType)))
        .finally(() => setActionLoading(false));
  }, [caseId, dispatch, isScience, menuType]);

  const handleDelMarkByBatch = () => {
    setActionLoading(true);
    isScience &&
      api
        .removeMarkByBatch({ caseId: caseId })
        .then(() => {
          handlePropertyList();
          handleDrawList();
        })
        .finally(() => setActionLoading(false));
  };

  const handleCheckMark = useCallback(
    (form, type) => {
      setActionLoading(true);
      api
        .checkMark({
          action: type,
          imageDataScienceCheckId: caseId,
          remark: form.content,
          accountIds: form.accountIds,
        })
        .then(res => {
          dispatch(getMarkPermissions(caseId, menuType));
          if (res.data) {
            // let text = '审核通过';
            message.success(applyText[type][1]);
          } else message.error(res.tip);
        })
        .then(handlePropertyList)
        .finally(() => {
          setActionLoading(false);
          closeApplyDialog();
        });
    },
    [caseId, closeApplyDialog, dispatch, handlePropertyList, menuType]
  );

  const [applyDialog, setApplyDialog] = useState(false);
  const [applyType, setApplyType] = useState(20);
  const closeApplyDialog = useCallback(() => setApplyDialog(false), []);
  const openApplyDialog = useCallback(
    type => () => {
      setApplyDialog(true);
      setApplyType(type);
    },
    []
  );

  /* ---------------------------------- 权限控制 ---------------------------------- */
  const menu = 'markTask_' + menuType;
  const data = 'markTask_' + caseId;
  const isMarkEdit = useHasPermission({ menu, action: 'showButton', data });
  const checkUserRole2 = useHasPermission({ menu, role: '2', data });
  const checkUserRole3 = useHasPermission({ menu, role: '3', data });
  const isCheckUser = checkUserRole2 || checkUserRole3;

  const actions = useSelector(({ permission }) =>
    permission.permissions.filter(
      item =>
        item.menu === menu &&
        item.data === data &&
        ![undefined, 'showButton'].includes(item.action)
    )
  );
  const applyTypeCode = {
    // 撤回通过
    25: 10,
    // 通过
    20: 20,
    // 驳回
    22: 30,
  };

  return (
    <div className="mark-list">
      <h1 className="viewer-mark-title mark-bg">
        <span>标注列表</span>
        <span>{pagination.total}</span>
      </h1>
      {apply && (
        <Alert
          style={{ marginBottom: '10px' }}
          showIcon
          type={
            ['30', '35'].includes(apply.applyStatusCode) ? 'error' : 'success'
          }
          message={
            <>
              {['30', '35'].includes(apply.applyStatusCode) &&
                apply.tagListReject && (
                  <div>
                    驳回:
                    {apply.tagListReject.map(item => item.userName).join(',')}
                  </div>
                )}
              <div>说明: {apply.applyRemark}</div>
            </>
          }
        />
      )}
      <div className="mark-filter">
        <span>标注者:</span>
        <Select
          size="small"
          mode="multiple"
          placeholder="可根据标注者进行筛选"
          allowClear
          defaultValue={markUsers}
          onChange={genMarkUsers}
        >
          {markUserOptions.map(item => (
            <Select.Option key={item.accountId} value={item.accountId}>
              {item.userName}
            </Select.Option>
          ))}
        </Select>
      </div>
      <div className="mark-hide">
        <Checkbox
          indeterminate={markHide.indeterminate}
          checked={markHide.all}
          onChange={handleHideStateChange(markHide, 'isHideMark')}
        >
          隐藏轮廓({hideList.length})
        </Checkbox>
        <Checkbox
          indeterminate={listHide.indeterminate}
          checked={listHide.all}
          onChange={handleHideStateChange(listHide, 'isHideList')}
        >
          隐藏列表({hideList.length})
        </Checkbox>
      </div>
      <div className="mark-list-main">
        <Spin spinning={propertyListLoading}>
          <Checkbox.Group
            style={{ display: 'block' }}
            className="mark-items"
            value={hideList}
            onChange={checkChange}
          >
            {filterMarkList.map((item, index) => (
              <div
                className={`mark-item${
                  activeMarkUUID === item.source.dataUuid ? ' active' : ''
                }`}
                key={item.source.id}
              >
                <h2>
                  {`${item.source.img}-${item.source.orderNum || ''}`}
                  <Checkbox value={item.source.id} />
                </h2>
                <Mark
                  item={item}
                  disabled={!isMarkEdit}
                  onChange={onChange}
                  onClick={() => onMarkClick(item.source)}
                />
              </div>
            ))}
          </Checkbox.Group>
        </Spin>
      </div>
      <Pagination
        className="mark-pagination"
        size="small"
        {...pagination}
        onChange={genSetPagination}
      />
      {isScience && (
        <div className="mark-list-footer action">
          <BackRoute route={{ pathname: '/research' }}>返回</BackRoute>

          {actions.map(({ payload: action }, index) => (
            <Button
              key={index}
              type="primary"
              danger={action.applyStatusCode === '22'}
              loading={actionLoading}
              onClick={
                action.applyStatusCode === '10'
                  ? handleUpdateMarkEnd
                  : openApplyDialog(applyTypeCode[action.applyStatusCode])
              }
            >
              {action.actionName}
            </Button>
          ))}

          {isMarkEdit && (
            <Popconfirm
              title="确定删除全部标注吗?"
              onConfirm={handleDelMarkByBatch}
              // onCancel={cancel}
              okText="确定"
              cancelText="取消"
            >
              <Button type="primary" danger loading={actionLoading}>
                全部删除
              </Button>
            </Popconfirm>
          )}

          {isCheckUser && (
            <ApplyDialog
              visible={applyDialog}
              type={applyType}
              users={markUserOptions}
              onCancel={closeApplyDialog}
              onChange={handleCheckMark}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default MarkList;
